import { ethers } from 'ethers';
import { LOCAL_STORAGE, LocalStorage, WalletHelper } from '@blink/components/src/utils';
import { NETWORK } from '@blink/components/src/constants/global';
import Logger from '@blink/components/src/utils/logger';
import { WALLET } from '@blink/components/src/types';

import { hideModalAction } from '@/shared/store/modals/actions';
import { store } from '@/shared/store';
import { disconnectWalletSaga } from '@/shared/store/wallets/actions';
import { toast } from '@/shared/hooks/useToast';
/**
 * Utility to safely get a signer from a wallet provider.
 *
 * @param walletProvider The wallet provider obtained from the wallet.
 * @param blockchainName The blockchain network name.
 * @returns Promise<ethers.Signer | null> A signer instance or null in case of failure.
 */
export async function getSafeSigner(
    walletProvider: any,
    blockchainName: NETWORK,
): Promise<ethers.Signer | null> {
    try {
        if (!walletProvider) {
            throw new Error('Wallet provider is undefined or null');
        }

        const provider = await getProvider(walletProvider, blockchainName);

        if (provider) {
            const signer: ethers.Signer = await provider.getSigner();
            return signer;
        }

        return null;
    } catch (error: any) {
        Logger.error('Error getting signer:', { error });

        throw error;
    }
}

async function getProvider(walletProvider: any, blockchainName: NETWORK) {
    if (!walletProvider) {
        Logger.error('Wallet provider is undefined or null');
        return null;
    }

    const walletType: string = LocalStorage.getItem(LOCAL_STORAGE.WALLET) || '';

    const handleSessionDelete = async () => {
        await walletProvider?.disconnect();

        store.dispatch(disconnectWalletSaga());
        store.dispatch(hideModalAction());

        toast({
            title: 'Wallet is not connected',
            variant: 'error',
        });
    };

    if (walletType === WALLET.WALLET_CONNECT) {
        walletProvider.on('session_delete', handleSessionDelete);
        walletProvider.on('disconnect', handleSessionDelete);
    }

    if (!walletProvider.connected && walletType === WALLET.WALLET_CONNECT) {
        await handleSessionDelete();

        throw new Error('Wallet is not connected');
    }

    const provider = new ethers.BrowserProvider(
        walletProvider,
        WalletHelper.getNetworkNumber(blockchainName),
    );

    if (
        (!walletProvider?.accounts || walletProvider?.accounts?.length === 0) &&
        walletType === WALLET.WALLET_CONNECT
    ) {
        Logger.error('Wallet is not connected: No accounts found');

        await handleSessionDelete();

        throw new Error('Wallet is not connected');
    }

    return provider;
}
